// The landing a family sees on a case before Dispatch (Lead or Case
// Secured). Pure: which cases get it, and its copy.
// The copy promises no response time; staff commit to one separately.

import type { LeadLandingStep } from "components/LeadLanding";
import { FIRST_CALL_PHONE_LABEL } from "constants/phones";

export type LeadCaseLandingModel = {
  decedentName: string | null;
  readyForCare: boolean;
  /** The person has not died: the case is a pre-registration. */
  imminent: boolean;
};

type LeadCaseFields = {
  stage?: string | null;
  lead_ready_for_care?: boolean | null;
  dcd_full_name?: string | null;
  labels?: string[] | null;
};

const PRE_DISPATCH_STAGES: readonly string[] = ["Lead", "Case Secured"];

/** A case gets the landing while it sits at Lead or Case Secured, whoever
 * created it. */
export function leadCaseLandingFor(
  caseData: LeadCaseFields,
): LeadCaseLandingModel | null {
  if (!caseData.stage || !PRE_DISPATCH_STAGES.includes(caseData.stage)) {
    return null;
  }
  const name = caseData.dcd_full_name?.trim();
  return {
    decedentName: name ? name : null,
    readyForCare: caseData.lead_ready_for_care === true,
    imminent: caseData.labels?.includes("Imminent") === true,
  };
}

export type LeadCaseCopy = {
  title: string;
  body: string;
  steps: LeadLandingStep[];
};

function imminentCaseCopy(model: LeadCaseLandingModel): LeadCaseCopy {
  const who = model.decedentName ?? "your loved one";
  return {
    title: model.decedentName
      ? `Planning ahead for ${model.decedentName}`
      : "Planning ahead",
    body: "Pre-registering costs nothing. A care specialist will call to talk through next steps.",
    steps: [
      {
        title: "A care specialist will call",
        body: `To get to know ${who} and any wishes.`,
      },
      {
        title: "Save our number",
        body: `${FIRST_CALL_PHONE_LABEL}. Call us first when the time comes, day or night.`,
      },
      {
        title: "Plan what you can, at your pace",
        body: "Wishes, documents, and who to notify, all in your account.",
      },
    ],
  };
}

export function leadCaseCopy(model: LeadCaseLandingModel): LeadCaseCopy {
  if (model.imminent) return imminentCaseCopy(model);
  const title = model.decedentName
    ? `Caring for ${model.decedentName}`
    : "Caring for your loved one";
  if (model.readyForCare) {
    return {
      title,
      body: "We're here for you. A care specialist will call you as soon as possible.",
      steps: [
        {
          title: "We call you",
          body: "To talk through next steps and answer your questions.",
        },
        {
          title: "We bring your loved one into our care",
          body: "Once we have talked with you.",
        },
        {
          title: "Finish arrangements online",
          body: "Paperwork, choices, and signatures in your account, at your pace.",
        },
      ],
    };
  }
  return {
    title,
    body: "We're here for you. Take the time you need. When you're ready, call us.",
    steps: [
      {
        title: "Call when you're ready",
        body: `Day or night, ${FIRST_CALL_PHONE_LABEL}.`,
      },
      {
        title: "Look around your account",
        body: "See what arrangements involve and what they cost.",
      },
      {
        title: "We'll check in gently",
        body: "We won't push. Reach out to us whenever you're ready.",
      },
    ],
  };
}
